import React, { useState, useEffect } from 'react'
import loadable from '@loadable/component'
import clsx from 'clsx'

import { CustomLink } from '@/components/atoms/CustomLink/CustomLink'
import { Image } from '@/components/atoms/Image/Image'
import { setViewHeight } from '@/helpers/setViewHeight'
import { ReactComponent as CloseIcon } from '@/assets/icons/X.svg'
import LogIn from '../LogIn'
import { NavigationProps } from './types'
import * as styles from './MobileNavigation.module.css'

const SearchBar = loadable(() => import('@/components/molecules/SearchBar/SearchBar'), {
  resolveComponent: (component) => component.SearchBar
})

export function MobileNavigation({
  resources,
  logo,
  handleOpen,
  isOpen,
  buttons,
  searchPlaceholder,
  searchNoResults,
  searchTitle,
  sectionTitle,
  language
}: NavigationProps) {
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [dropdownIndex, setIndex] = useState(null)
  const [searchOpen, toggleSearch] = useState(false)

  const toggleSearchBar = () => {
    toggleSearch(!searchOpen)
  }

  const handleDropdownOpen = (index: any) => {
    setIndex(index)
    if (isDropdownOpen && index !== dropdownIndex) {
      setDropdownOpen(false)
    }
    setDropdownOpen((val) => !val)
  }

  useEffect(() => {
    window.addEventListener('resize', setViewHeight)
    return () => {
      window.removeEventListener('resize', setViewHeight)
    }
  }, [])

  return (
    <div>
      {isOpen && (
        <div className="flex flex-col w-full fixed top-0 left-0 z-10 bg-white overflow-hidden h-full">
          <div
            className={clsx(
              'flex justify-between items-center  flex-shrink-0 relative p-24',
              !searchOpen ? 'px-24' : 'px-10'
            )}
          >
            {!searchOpen && (
              <CustomLink
                is_external={false}
                link_url={{ cached_url: '', id: 'logo-link', url: '', fieldtype: 'multilink', linktype: '' }}
                new_tab={false}
              >
                <Image fixed data={logo} options={{ width: 103 }} className="w-131" />
              </CustomLink>
            )}
            <div className={clsx('flex', searchOpen ? 'w-full' : 'w-auto')}>
              <div className={clsx('relative w-full flex align-middle', styles.searchHolder, searchOpen && 'h-48')}>
                <SearchBar
                  isOpen={searchOpen}
                  onClick={toggleSearchBar}
                  placeholder={searchPlaceholder}
                  noResults={searchNoResults}
                  sectionTitle={sectionTitle}
                  language={language}
                  iconStyles="fa fa-search !top-16 !tablet:top-20 border-red"
                  searchRedirect="help/search/"
                  suggestionIndexName={process.env.GATSBY_ALGOLIA_INDEX_HELP || ''}
                />
              </div>
              <button onClick={handleOpen} type="button">
                <CloseIcon />
                {/* <i className="fa fa-2x fa-times text-text-dark-900 ml-20" aria-hidden="true" /> */}
              </button>
            </div>
          </div>
          <hr className={clsx('border-t-2 w-[calc(100%-48px)] border-grey-100 mx-auto', searchOpen && 'hidden')} />
          <p className={clsx('hidden text-bodyL font-bold text-grey-700 text-center', searchOpen && 'block')}>
            {searchTitle}
          </p>
          <nav
            className={clsx(
              'flex flex-col justify-between pt-24 overflow-y-auto h-full',
              searchOpen ? 'hidden' : 'flex'
            )}
          >
            {resources?.items.length && (
              <ul className="flex flex-col">
                {resources?.items.map((topic, index) => (
                  <li className="font-bold text-text-dark-900 text-bodyL mb-20 text-left" key={topic._uid}>
                    <div>
                      <span
                        onClick={() => handleDropdownOpen(index)}
                        className="flex items-center justify-between cursor-pointer px-24
                        text-bodyL text-primary-600 font-mulish"
                        aria-hidden="true"
                      >
                        {topic.title[0]?.text}
                        {isDropdownOpen && index === dropdownIndex ? (
                          <i className="fa fa-chevron-up ml-20 netbook:ml-32" aria-hidden="true" />
                        ) : (
                          <i className="fa fa-chevron-down ml-20 netbook:ml-32" aria-hidden="true" />
                        )}
                      </span>
                      {isDropdownOpen && index === dropdownIndex && (
                        <ul className="list-disc block ml-44 pr-40">
                          {topic.links &&
                            topic.links.map((link) => (
                              <li>
                                <CustomLink
                                  {...link}
                                  is_external={false}
                                  new_tab={false}
                                  className="text-bodyM font-medium mt-16 font-mulish text-black-dark"
                                  activeClassName="underline text-primary-600"
                                >
                                  {link.text}
                                </CustomLink>
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-20 w-full px-24 py-16">
              <hr className="border-t-2 border-light mb-16" />
              <div className="flex flex-col">
                {resources?.additional_links?.map((link) => (
                  <CustomLink {...link} className="text-primary-600 text-bodyL font-bold mb-24">
                    {link.text}
                  </CustomLink>
                ))}
              </div>
              <LogIn buttons={buttons} />
            </div>
          </nav>
        </div>
      )}
    </div>
  )
}
